import { useInfiniteQuery } from '@tanstack/react-query'
import {
  Flex,
  Typography,
  FormInstance,
  Select,
  Tag,
  Checkbox,
  Empty,
  Spin,
  Form,
  Card,
  Input
} from 'antd'
import { AnyObject } from 'antd/es/_util/type'
import { DefaultOptionType } from 'antd/es/select'
import { useTheme } from 'antd-style'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'

import {
  useClassifierServiceGetCommonClassifierElementListEndpointKey,
  usePatientTreatmentServiceGetPatientTreatmentPlanAdverseEffectEntryEndpoint
} from 'src/api/queries'
import {
  ClassifierCategory,
  ClassifierService,
  ClassifierType,
  GetAdverseEffectEntryAdverseEffectListResponse2,
  GetCommonClassifierElementResponse
} from 'src/api/requests'
import { DEFAULT_PER_PAGE, requiredRules } from 'src/app-constants'
import { EditableFormItem } from 'src/components'
import { useClassifierId } from 'src/hooks'
import { useLocales } from 'src/locales'

import { useStyles } from './styles'

const { Text } = Typography

type Props = {
  form?: FormInstance
  name: number
  isEditing: boolean
  selectedItems?: AnyObject
  setSelectedItems: (value: AnyObject) => void
  getReactionId: (form?: FormInstance, name?: number) => string
  getReportedBy: string
}

export const ReactionsListChildren = ({
  form,
  name,
  isEditing,
  selectedItems,
  setSelectedItems,
  getReactionId,
  getReportedBy
}: Props) => {
  const { t, currentLang } = useLocales()
  const { id } = useParams()

  const { styles } = useStyles()

  const palette = useTheme()
  const { data: adverseReactionsDetails, isLoading } =
    usePatientTreatmentServiceGetPatientTreatmentPlanAdverseEffectEntryEndpoint(
      {
        id: String(id),
        adverseEffectEntryId: getReactionId(form, name)
      }
    )

  const onTagSelectionChange = (index: number, value: string) => {
    setSelectedItems((prev: AnyObject) => ({ ...prev, [index]: value }))
    form?.setFieldsValue({
      events: {
        ...form?.getFieldValue('events'),
        [index]: {
          ...form.getFieldValue(['events', index]),
          adverseEffects: value
        }
      }
    })
  }

  const removeTag = (index: number, item: DefaultOptionType) => {
    setSelectedItems((prev: AnyObject) => {
      const updatedItems =
        prev?.[index]?.filter(
          (selectedItem: DefaultOptionType) =>
            selectedItem?.value !== item?.value
        ) || []
      form?.setFieldsValue({
        events: {
          ...form?.getFieldValue('events'),
          [index]: {
            ...form?.getFieldValue(['events', index]),
            adverseEffects: updatedItems
          }
        }
      })
      return { ...prev, [index]: updatedItems }
    })
  }

  const classifierId = useClassifierId(
    ClassifierType.ACCUSE,
    ClassifierCategory.TREATMENT
  )

  useEffect(() => {
    if (adverseReactionsDetails) {
      form?.setFieldsValue({
        events: {
          [name]: {
            comments: adverseReactionsDetails?.comments
          }
        }
      })
    }
  }, [adverseReactionsDetails])

  const dropdownRender = (name: number) => (
    <Flex vertical gap={4} className={styles.dropdownRender}>
      {options?.map((option) => (
        <Checkbox
          key={option.value}
          checked={
            selectedItems?.[name]?.some(
              (item: DefaultOptionType) => item?.value === option?.value
            ) || false
          }
          onChange={(e) => {
            const existingItems = selectedItems?.[name] ?? []
            const newValue = e.target.checked
              ? [...existingItems, option]
              : selectedItems?.[name]?.filter(
                  (item: DefaultOptionType) => item.value !== option.value
                )
            onTagSelectionChange(name, newValue)
          }}
        >
          {option.label}
        </Checkbox>
      ))}
    </Flex>
  )

  const {
    data: accusesList,
    fetchNextPage,
    isFetching
  } = useInfiniteQuery({
    queryKey: [
      useClassifierServiceGetCommonClassifierElementListEndpointKey,
      String(classifierId)
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response =
        await ClassifierService.getCommonClassifierElementListEndpoint(
          String(classifierId),
          pageParam,
          DEFAULT_PER_PAGE
        )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data: GetCommonClassifierElementResponse[] | undefined
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1,
    enabled: !!classifierId
  })

  const options = accusesList?.pages.flatMap(
    (page) =>
      page.data?.map((item: GetCommonClassifierElementResponse) => ({
        value: item?.id,
        label: item?.translation?.[currentLang?.value]
      })) || []
  )

  return (
    <Card
      bordered={false}
      className={styles.collapseChildren}
      loading={isLoading}
    >
      <Flex vertical gap={16}>
        {isEditing ? (
          <Form.Item
            name={[name, 'adverseEffects']}
            label={t('consultations.accuse')}
          >
            <Select
              allowClear
              filterOption={false}
              options={options}
              loading={isFetching}
              showSearch={false}
              className={styles.adverseField}
              placeholder={t('common.select_from_list')}
              dropdownRender={() => dropdownRender(name)}
              onPopupScroll={(e) => {
                const target = e.target as HTMLElement
                if (
                  target.scrollTop + target.offsetHeight ===
                  target.scrollHeight
                )
                  fetchNextPage()
              }}
              notFoundContent={
                isFetching ? (
                  <Flex justify="center">
                    <Spin spinning />
                  </Flex>
                ) : (
                  <Empty />
                )
              }
            />
            <Flex className={styles.tagsWrapper}>
              {selectedItems?.[name]?.map((item: DefaultOptionType) => (
                <Tag
                  key={item.value}
                  closable
                  className={styles.tags}
                  onClose={() => removeTag(name, item)}
                >
                  {item?.label}
                </Tag>
              ))}
            </Flex>
          </Form.Item>
        ) : (
          <Flex gap={4}>
            <Text strong>{t('consultations.accuse')}:</Text>
            <Text>
              {form
                ?.getFieldValue(['events', name, 'adverseEffects'])
                ?.map(
                  (item: GetAdverseEffectEntryAdverseEffectListResponse2) =>
                    item?.translation?.[currentLang?.value]
                )
                .join(', ') || '-'}
            </Text>
          </Flex>
        )}

        <EditableFormItem
          name={[name, 'comments']}
          label={`${t('common.comment')}:`}
          isEditing={isEditing}
          rules={requiredRules}
        >
          <Input.TextArea rows={5} />
        </EditableFormItem>

        <Text
          style={{
            color: palette.colorTextTertiary
          }}
        >
          {getReportedBy}
        </Text>
      </Flex>
    </Card>
  )
}
